/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Layout, message, Modal } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useResumeStore } from "../../store/resumeStore";
import useReloadRedirect from "../../hooks/useReloadRedirect";
import useViewportWidth from "../../hooks/useViewportWidth";
import { isResumeEmpty } from "../main/mapParsedResumeToState";
import {
  getTemplateOptionsForExperienceLevel,
  normalizeExperienceLevel,
} from "./templateCatalog";
import {
  logTemplateSelection,
  readTemplateIdFromSearch,
  withTemplateIdInSearch,
} from "./templateSelection";
import { getMissingRequiredFields, validateResume } from "./resumeValidation";
import { API } from "../../EndpPoints";
import PaymentStatus from "./PaymentStatus";
import {
  authenticatedFetch,
  buildDownloadPurchaseContext,
} from "./paymentAccess";
import useSubscriptionStatus from "../Subscription/useSubscriptionStatus";
import TemplateRenderer from "./templateRenderer";
import TemplateLivePreview from "./TemplateLivePreview";
// console.log(API);

const FIXED_A4_WIDTH_MM = 210;
const BASE_A4_HEIGHT_MM = 297;
const PDF_EXPORT_MARGIN_TOP_MM = 4;
const PDF_EXPORT_MARGIN_BOTTOM_MM = 6;
const PDF_EXPORT_KEEP_TOGETHER_SELECTORS = [
  ".cv-section[data-cv-keep='whole']",
  ".cv-heading",
  ".headerBox",
  "[data-section='educationItem']",
  "[data-section='awardItem']",
  "[data-section='recognitionItem']",
  ".eduRowBlock",
].join(", ");
const PDF_EXPORT_SPLITTABLE_SELECTORS = [
  "[data-section='experienceItem']",
  "[data-section='projectItem']",
  "[data-section='summaryItem']",
  ".exp-block",
  ".companyBlock",
  ".timelineItem",
  ".sectionBlock",
  ".cv-group",
  "[data-cv-group='true']",
].join(", ");
const TEMPLATE_PRINT_CONFIG = {
  default: {
    pageMargin: "4mm 0 6mm 0",
    contentWidth: "210mm",
    contentMaxWidth: "210mm",
    contentPadding: "0",
    extraCss: "",
  },
  template1: {
    pageMargin: "4mm 0 6mm 0",
    contentWidth: "210mm",
    contentMaxWidth: "210mm",
    contentPadding: "0",
    extraCss: "",
  },
  template2: {
    pageMargin: "4mm 0 6mm 0",
    contentWidth: "210mm",
    contentMaxWidth: "210mm",
    contentPadding: "0",
    extraCss: "",
  },
};

function readStyleValue(primaryValue, fallbackValue) {
  if (typeof primaryValue === "string" && primaryValue.trim()) {
    return primaryValue.trim();
  }

  if (typeof fallbackValue === "string" && fallbackValue.trim()) {
    return fallbackValue.trim();
  }

  return "";
}

function normalizePrintStyleValue(value, fallbackValue) {
  const resolvedValue = readStyleValue(value, fallbackValue);

  if (!resolvedValue) return "";
  if (resolvedValue === "fit-content") return "auto";
  if (resolvedValue === "normal") return "";

  return resolvedValue;
}

function getSheetNode(root) {
  if (!root) return null;

  return (
    root.querySelector(
      ':scope > div:first-child, :scope > main:first-child, :scope > section:first-child',
    ) || root
  );
}

function waitForPaint(frames = 2) {
  return new Promise((resolve) => {
    const step = () => {
      if (frames <= 0) {
        resolve();
        return;
      }

      frames -= 1;
      window.requestAnimationFrame(step);
    };

    window.requestAnimationFrame(step);
  });
}

function normalizeHighStyleExportLayout(root) {
  const highStyleRoot =
    root?.querySelector?.("[data-template-family='high-style']") ||
    (root?.matches?.("[data-template-family='high-style']") ? root : null);

  if (!highStyleRoot) return;

  const body = highStyleRoot.querySelector("[data-high-style-body]");
  if (body) {
    body.style.minHeight = "auto";
    body.style.height = "auto";
    body.style.maxHeight = "none";
    body.style.overflow = "visible";
    body.style.alignItems = "start";
  }

  highStyleRoot
    .querySelectorAll("[data-high-style-main], [data-high-style-sidebar]")
    .forEach((column) => {
      column.style.minHeight = "auto";
      column.style.height = "auto";
      column.style.maxHeight = "none";
      column.style.overflow = "visible";
    });
}

function sanitizePdfExportNode(root) {
  if (!root) return;

  const resumeRoot =
    root.querySelector?.("[data-resume-root='true']") ||
    (root?.matches?.("[data-resume-root='true']") ? root : null) ||
    root;
  const preserveHighStyleVisuals = Boolean(
    root.querySelector?.("[data-template-family='high-style']") ||
      root.matches?.("[data-template-family='high-style']"),
  );

  root.setAttribute?.("data-pdf-export", "true");
  resumeRoot?.setAttribute?.("data-pdf-export", "true");

  root.querySelectorAll("[style]").forEach((node) => {
    if (!node?.style) return;

    const preserveAbsolutePosition =
      node.getAttribute("data-pdf-preserve-absolute") === "true" ||
      Boolean(node.closest?.("[data-pdf-preserve-absolute='true']"));

    if (!preserveHighStyleVisuals && node.style.position === "absolute" && !preserveAbsolutePosition) {
      const hasVisibleContent = Boolean((node.textContent || "").trim());
      const hasMedia = Boolean(node.querySelector?.("img, svg, canvas"));
      const isDecorative = node.getAttribute("aria-hidden") === "true" || (!hasVisibleContent && !hasMedia);

      if (isDecorative) {
        node.setAttribute("data-export-decorative", "true");
        node.style.display = "none";
      } else {
        node.style.position = "static";
        node.style.top = "";
        node.style.right = "";
        node.style.bottom = "";
        node.style.left = "";
        node.style.inset = "";
      }
    }

    if (node.style.float) {
      node.style.float = "none";
    }

    if (node.style.whiteSpace && node.style.whiteSpace !== "normal") {
      node.style.whiteSpace = "normal";
    }

    if (node.style.overflowWrap === "anywhere") {
      node.style.overflowWrap = "break-word";
    }

    if (node.style.wordBreak && node.style.wordBreak !== "normal") {
      node.style.wordBreak = "normal";
    }

    if (node.style.wordSpacing && node.style.wordSpacing !== "normal") {
      node.style.wordSpacing = "normal";
    }

  });

  root.querySelectorAll(".cv-rich-text-inline").forEach((node) => {
    node.style.display = "block";
    node.style.width = "100%";
    node.style.whiteSpace = "normal";
  });

  root
    .querySelectorAll("[data-section='experienceItem'] > span:first-child")
    .forEach((node) => {
      if (node.getAttribute("data-field")) return;
      if ((node.textContent || "").trim()) return;
      node.setAttribute("data-export-decorative", "true");
      node.style.display = "none";
    });

  root
    .querySelectorAll("[data-section='experienceItem'] > div:first-of-type")
    .forEach((node) => {
      node.style.paddingLeft = "0";
    });
}

async function buildMeasuredExportNode(previewNode) {
  const exportNode = buildExportNode(previewNode);
  if (!exportNode || typeof document === "undefined") {
    return exportNode;
  }

  const sandbox = document.createElement("div");
  sandbox.style.position = "fixed";
  sandbox.style.left = "-100000px";
  sandbox.style.top = "0";
  sandbox.style.width = `${FIXED_A4_WIDTH_MM}mm`;
  sandbox.style.background = "#ffffff";
  sandbox.style.pointerEvents = "none";
  sandbox.style.zIndex = "-1";
  sandbox.appendChild(exportNode);
  document.body.appendChild(sandbox);

  await waitForPaint(2);
  stabilizeEducationPageBreaks(exportNode);
  await waitForPaint(1);
  sandbox.remove();

  return exportNode;
}

function getEducationBlocks(root) {
  const selectors = [
    '[data-cv-kind="educationSection"]',
    '[data-section="educationSection"]',
    'section[data-section="education"]',
    '.cv-section[data-section="education"]',
  ].join(", ");

  return Array.from(root.querySelectorAll(selectors)).filter((node) => {
    const text = (node.textContent || "").trim();
    return /education|study|degree|university|college|school/i.test(text);
  });
}

function stabilizeEducationPageBreaks(root) {
  if (!root || typeof window === "undefined") return;

  const widthPx = root.getBoundingClientRect?.().width || root.offsetWidth || 0;
  const pxPerMm = widthPx > 0 ? widthPx / FIXED_A4_WIDTH_MM : 96 / 25.4;
  const printableHeightPx =
    (BASE_A4_HEIGHT_MM - PDF_EXPORT_MARGIN_TOP_MM - PDF_EXPORT_MARGIN_BOTTOM_MM) *
    pxPerMm;
  const rootTop = root.getBoundingClientRect?.().top || 0;

  getEducationBlocks(root).forEach((node) => {
    const rect = node.getBoundingClientRect?.();
    if (!rect || rect.height <= 0) return;

    node.setAttribute("data-pdf-education-block", "true");
    node.style.breakInside = "avoid";
    node.style.pageBreakInside = "avoid";

    if (rect.height >= printableHeightPx * 0.85) return;

    const top = rect.top - rootTop;
    const bottom = rect.bottom - rootTop;
    const startPage = Math.floor(Math.max(top, 0) / printableHeightPx);
    const endPage = Math.floor(Math.max(bottom - 1, 0) / printableHeightPx);
    const remainingPageHeight = printableHeightPx - (Math.max(top, 0) % printableHeightPx);
    const hasEnoughRoom = remainingPageHeight >= rect.height + pxPerMm * 6;

    if (startPage !== endPage || !hasEnoughRoom) {
      node.setAttribute("data-pdf-education-page-break", "true");
      node.style.breakBefore = "page";
      node.style.pageBreakBefore = "always";
    }
  });
}

function optimizeExportPageFlow(root) {
  if (!root || typeof window === "undefined") return;

  const resumeRoot =
    root.querySelector?.("[data-resume-root='true']") ||
    root.querySelector?.("[data-cv-root='true']") ||
    root;
  const widthPx = root.getBoundingClientRect?.().width || root.offsetWidth || 0;
  const pxPerMm = widthPx > 0 ? widthPx / FIXED_A4_WIDTH_MM : 96 / 25.4;
  const printableHeightPx =
    (BASE_A4_HEIGHT_MM - PDF_EXPORT_MARGIN_TOP_MM - PDF_EXPORT_MARGIN_BOTTOM_MM) *
    pxPerMm;
  const tallBlockThresholdPx = printableHeightPx * 0.28;
  const oversizedBlockThresholdPx = printableHeightPx * 0.42;
  const oversizedExperienceThresholdPx = printableHeightPx * 0.88;
  const compactThresholdPx = pxPerMm * 38;

  Array.from(resumeRoot.querySelectorAll(PDF_EXPORT_KEEP_TOGETHER_SELECTORS)).forEach((node) => {
    node.style.breakInside = "avoid";
    node.style.pageBreakInside = "avoid";
  });

  const splittableNodes = Array.from(
    resumeRoot.querySelectorAll(PDF_EXPORT_SPLITTABLE_SELECTORS),
  );

  splittableNodes.forEach((node) => {
    const rect = node.getBoundingClientRect?.();
    const height = rect?.height || node.offsetHeight || 0;
    const bulletCount = node.querySelectorAll?.("li")?.length || 0;
    const paragraphCount = node.querySelectorAll?.("p")?.length || 0;
    const hasRichText = Boolean(node.querySelector?.("ul, ol, .cv-rich-text, .cv-rich-text-inline"));
    const isExperienceLike = Boolean(
      node.matches?.(
        "[data-section='experienceItem'], [data-section='projectItem'], .exp-block, .companyBlock, .timelineItem",
      ),
    );
    const shouldSplit = isExperienceLike
      ? height >= oversizedExperienceThresholdPx
      : height >= oversizedBlockThresholdPx ||
        (height >= tallBlockThresholdPx && (bulletCount >= 3 || paragraphCount >= 2 || hasRichText));

    if (!shouldSplit) {
      if (isExperienceLike && height > 0) {
        node.removeAttribute("data-cv-split-allowed");
        node.style.breakInside = "avoid";
        node.style.pageBreakInside = "avoid";
      }
      return;
    }

    node.dataset.cvSplitAllowed = "true";
    node.style.breakInside = "auto";
    node.style.pageBreakInside = "auto";
    node.style.marginBottom = node.style.marginBottom || "0";
  });

  const sectionCandidates = Array.from(
    resumeRoot.querySelectorAll("[data-cv-section='true'], .cv-section"),
  ).filter((node) => !node.closest("[data-cv-group='true'] [data-cv-section='true']"));

  let remainingHeight = printableHeightPx;
  sectionCandidates.forEach((node) => {
    const height = node.getBoundingClientRect?.().height || node.offsetHeight || 0;

    if (remainingHeight < compactThresholdPx) {
      node.setAttribute("data-pdf-compact", "true");
    }

    if (height > remainingHeight && remainingHeight < printableHeightPx) {
      node.setAttribute("data-pdf-compact", "true");
      remainingHeight = printableHeightPx;
    }

    if (height >= printableHeightPx) {
      remainingHeight = printableHeightPx;
      return;
    }

    remainingHeight -= height;

    if (remainingHeight < pxPerMm * 18) {
      remainingHeight = printableHeightPx;
    }
  });
}

function buildPdfPrintCss(previewNode, templateId) {
  const templateConfig =
    TEMPLATE_PRINT_CONFIG[templateId] || TEMPLATE_PRINT_CONFIG.default;
  const resolvedPageMargin =
    templateConfig.pageMargin || TEMPLATE_PRINT_CONFIG.default.pageMargin;
  const computedStyles = window.getComputedStyle(previewNode);
  const wrapperPadding = normalizePrintStyleValue(
    previewNode.style.padding,
    computedStyles.padding,
  );
  const wrapperBackground = normalizePrintStyleValue(
    previewNode.style.background,
    computedStyles.backgroundColor,
  );
  const wrapperBoxSizing = normalizePrintStyleValue(
    previewNode.style.boxSizing,
    computedStyles.boxSizing,
  );
  const contentWidth =
    templateConfig.contentWidth || "210mm";
  const contentMaxWidth = templateConfig.contentMaxWidth || contentWidth;
  const contentPadding =
    templateConfig.contentPadding || wrapperPadding || "1.5mm";
  const highStylePrintOverrides = `
[data-template-family="high-style"] {
  padding: 0 !important;
  min-height: auto !important;
  height: auto !important;
  overflow: visible !important;
}

[data-template-family="high-style"] [data-high-style-body] {
  display: flex !important;
  flex-direction: row !important;
  gap: 20px !important;
  align-items: flex-start !important;
  min-height: auto !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}

[data-template-family="high-style"] [data-high-style-main],
.cv-main {
  display: block !important;
  flex: 0 0 65% !important;
  width: 65% !important;
  min-width: 0 !important;
  align-self: start !important;
  min-height: auto !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}

[data-template-family="high-style"] [data-high-style-sidebar],
.cv-sidebar {
  display: block !important;
  flex: 0 0 35% !important;
  width: 35% !important;
  min-width: 0 !important;
  align-self: start !important;
  min-height: auto !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}

[data-template-family="high-style"] [data-high-style-main] > *,
[data-template-family="high-style"] [data-high-style-sidebar] > *,
.cv-main > *,
.cv-sidebar > * {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
}
`;
  return `
<style id="pdf-print-rules" data-template="${templateId}">
@page {
  size: A4;
  margin: ${resolvedPageMargin};
}

html,
body {
  margin: 0;
  padding: 0;
  background: #ffffff;
  overflow-x: hidden !important;
  line-height: 1.5 !important;
  word-break: normal !important;
  overflow-wrap: break-word !important;
  white-space: normal !important;
}

.cv-document {
  width: ${contentWidth} !important;
  max-width: ${contentMaxWidth} !important;
  margin: 0 auto !important;
  padding: 0 !important;
  background: transparent !important;
  box-sizing: border-box !important;
}

.page,
.cv-page {
  width: ${contentWidth} !important;
  max-width: ${contentMaxWidth} !important;
  min-height: auto !important;
  height: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  background: #ffffff !important;
  overflow: visible !important;
  box-sizing: border-box !important;
}

.resume-print {
  width: 100% !important;
  max-width: 100% !important;
  min-height: auto !important;
  height: auto !important;
  margin: 0 !important;
  padding: ${contentPadding} !important;
  background: ${wrapperBackground || "#ffffff"} !important;
  box-sizing: ${wrapperBoxSizing || "border-box"} !important;
  box-shadow: none !important;
  border: 0 !important;
  transform: none !important;
  transform-origin: top center !important;
  overflow: visible !important;
}

body,
body * {
  font-kerning: normal !important;
}

p,
span,
div,
li {
  word-break: normal !important;
  overflow-wrap: break-word !important;
  white-space: normal !important;
}

ul,
ol {
  padding-left: 16px !important;
  margin: 6px 0 0 !important;
}

li {
  margin-bottom: 4px !important;
}

li:last-child {
  margin-bottom: 0 !important;
}

h1,
h2,
h3 {
  margin: 0 0 8px !important;
  line-height: 1.25 !important;
}

.page p,
.page li,
.cv-page p,
.cv-page li,
p,
li {
  orphans: 3;
  widows: 3;
}

.page > [data-resume-root="true"],
.cv-page > [data-resume-root="true"],
[data-resume-root="true"] {
  min-height: auto !important;
  height: auto !important;
  box-sizing: border-box !important;
  overflow: visible !important;
}

[data-resume-root="true"] > div:first-child,
[data-resume-root="true"] > main:first-child,
[data-resume-root="true"] > section:first-child {
  min-height: auto !important;
  height: auto !important;
  box-sizing: border-box !important;
  overflow: visible !important;
}

[data-resume-root="true"] [data-section] {
  max-height: none !important;
  height: auto !important;
  overflow: visible !important;
}

[data-pdf-export="true"],
[data-pdf-export="true"] * {
  white-space: normal !important;
  word-break: normal !important;
  overflow-wrap: break-word !important;
}

[data-pdf-export="true"] [style*="overflow-wrap:anywhere"],
[data-pdf-export="true"] [style*="overflow-wrap: anywhere"] {
  word-break: normal !important;
  overflow-wrap: break-word !important;
}

[data-pdf-export="true"] [style*="float:"],
[data-pdf-export="true"] [style*="float: "] {
  float: none !important;
}

[data-pdf-export="true"] .cv-rich-text-inline {
  display: block !important;
  width: 100% !important;
}

[data-pdf-export="true"] .cv-rich-text-inline p,
[data-pdf-export="true"] .cv-rich-text-inline ul,
[data-pdf-export="true"] .cv-rich-text-inline ol {
  margin: 0 !important;
}

${highStylePrintOverrides}

[data-resume-root="true"] article,
[data-resume-root="true"] section,
[data-resume-root="true"] [data-section],
[data-resume-root="true"] [data-section] > * {
  max-height: none !important;
}

[data-resume-root="true"]:not([data-template-family="new-prototype"]) section,
[data-resume-root="true"]:not([data-template-family="new-prototype"]) .cv-section,
[data-high-style-section="true"],
[data-resume-root="true"]:not([data-template-family="new-prototype"]) .section {
  border-top: 1px solid transparent !important;
  padding-top: 6px !important;
  margin-bottom: 12px !important;
  page-break-inside: auto !important;
}

[data-resume-root="true"]:not([data-template-family="new-prototype"]) section:first-child,
[data-resume-root="true"]:not([data-template-family="new-prototype"]) .cv-section:first-child,
[data-high-style-section="true"]:first-child,
[data-resume-root="true"]:not([data-template-family="new-prototype"]) .section:first-child {
  padding-top: 0 !important;
}

[data-resume-root="true"]:not([data-template-family="new-prototype"]) .cv-section + .cv-section,
[data-high-style-section="true"] + [data-high-style-section="true"],
[data-resume-root="true"]:not([data-template-family="new-prototype"]) .section + .section,
[data-resume-root="true"]:not([data-template-family="new-prototype"]) [data-section] + [data-section] {
  margin-top: 14px !important;
  border-top-color: rgba(148, 163, 184, 0.14) !important;
}

.exp-block,
[data-section="experienceItem"],
[data-section="projectItem"],
.cv-group,
[data-cv-group="true"],
.companyBlock,
.sectionBlock,
.timelineItem {
  page-break-inside: auto !important;
  break-inside: auto !important;
}

[data-pdf-export="true"] [data-section="experienceItem"]:not([data-cv-split-allowed="true"]),
[data-pdf-export="true"] [data-section="projectItem"]:not([data-cv-split-allowed="true"]),
[data-pdf-export="true"] .exp-block:not([data-cv-split-allowed="true"]),
[data-pdf-export="true"] .companyBlock:not([data-cv-split-allowed="true"]),
[data-pdf-export="true"] .timelineItem:not([data-cv-split-allowed="true"]) {
  page-break-inside: avoid !important;
  break-inside: avoid-page !important;
}

[data-pdf-export="true"] [data-cv-split-allowed="true"] {
  page-break-inside: auto !important;
  break-inside: auto !important;
}

.eduRowBlock,
.headerBox,
.cv-section[data-cv-keep="whole"],
.cv-heading,
[data-cv-kind="educationSection"],
[data-section="educationSection"],
[data-section="education"],
[data-section="educationItem"],
[data-section="awardItem"],
[data-section="recognitionItem"] {
  page-break-inside: avoid !important;
  break-inside: avoid-page !important;
}

[data-pdf-education-block="true"] {
  page-break-inside: avoid !important;
  break-inside: avoid-page !important;
}

[data-pdf-education-page-break="true"] {
  page-break-before: always !important;
  break-before: page !important;
}

[data-pdf-export="true"] [data-template-family="high-style"] [data-title="true"],
[data-pdf-export="true"] [data-template-family="high-style"] [data-title="true"] * {
  min-width: 0 !important;
  max-width: 100% !important;
  word-break: keep-all !important;
  overflow-wrap: normal !important;
  hyphens: none !important;
  writing-mode: horizontal-tb !important;
  text-orientation: mixed !important;
}

[data-pdf-export="true"] [data-template-family="high-style"] [data-title="true"] > span,
[data-pdf-export="true"] [data-template-family="high-style"] [data-title="true"] > div:first-child,
[data-pdf-export="true"] [data-template-family="high-style"] [data-title="true"] > div:last-child {
  white-space: nowrap !important;
}

[data-pdf-export="true"] [data-template-family="high-style"] [data-section="experienceItem"] > [data-export-decorative="true"] {
  display: none !important;
}

[data-pdf-export="true"] [data-pdf-compact="true"],
[data-pdf-export="true"] [data-pdf-compact="true"] [data-section="experienceItem"] {
  margin-bottom: 0 !important;
}

[data-pdf-export="true"] [data-pdf-compact="true"] [data-section="experienceItem"] {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

[data-pdf-export="true"] [data-pdf-compact="true"] ul,
[data-pdf-export="true"] [data-pdf-compact="true"] ol {
  margin-top: 4px !important;
}

[data-pdf-export="true"] [data-pdf-compact="true"] li {
  margin-bottom: 2px !important;
}

[data-pdf-export="true"] [data-pdf-compact="true"] [data-title="true"] {
  margin-bottom: 6px !important;
}

* {
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

${templateConfig.extraCss || ""}
</style>
`;
}

function buildExportNode(previewNode) {
  if (!previewNode) return null;

  const exportNode = previewNode.cloneNode(true);
  const cvDocument = exportNode.querySelector(".cv-document");
  const cvPage = exportNode.querySelector(".cv-page");
  const resumeRoot =
    exportNode.querySelector("[data-resume-root='true']") || exportNode;
  const highStyleRoot = exportNode.querySelector(
    "[data-template-family='high-style']",
  );

  if (cvDocument) {
    cvDocument.style.width = `${FIXED_A4_WIDTH_MM}mm`;
    cvDocument.style.maxWidth = `${FIXED_A4_WIDTH_MM}mm`;
    cvDocument.style.margin = "0 auto";
    cvDocument.style.padding = "0";
    cvDocument.style.overflow = "visible";
  }

  if (cvPage) {
    cvPage.style.width = `${FIXED_A4_WIDTH_MM}mm`;
    cvPage.style.maxWidth = `${FIXED_A4_WIDTH_MM}mm`;
    cvPage.style.minHeight = "auto";
    cvPage.style.height = "auto";
    cvPage.style.margin = "0";
    cvPage.style.padding = "0";
    cvPage.style.overflow = "visible";
    cvPage.style.background = "#ffffff";
  }

  resumeRoot.style.minHeight = "auto";
  resumeRoot.style.height = "auto";
  resumeRoot.style.overflow = "visible";
  resumeRoot.setAttribute("data-pdf-export", "true");

  const sheetNode = getSheetNode(resumeRoot);
  if (sheetNode) {
    sheetNode.style.minHeight = "auto";
    sheetNode.style.height = "auto";
    sheetNode.style.overflow = "visible";
    if (!highStyleRoot) {
      sheetNode.style.borderRadius = "0";
      sheetNode.style.boxShadow = "none";
    }
  }

  const body = resumeRoot.querySelector("[data-high-style-body]");
  if (body) {
    body.style.minHeight = "auto";
    body.style.height = "auto";
    body.style.overflow = "visible";
  }

  normalizeHighStyleExportLayout(exportNode);
  sanitizePdfExportNode(exportNode);

  if (!highStyleRoot && sheetNode) {
    const topLevelLayouts = Array.from(sheetNode.children).filter(
      (child) => child.children.length > 0,
    );

    topLevelLayouts.forEach((node) => {
      if (node.getBoundingClientRect) {
        node.style.minHeight = "auto";
        node.style.height = "auto";
      }
    });
  }

  return exportNode;
}

const { Content } = Layout;
export default function FinalExport() {
  useReloadRedirect({ route: "/export" });
  const navigate = useNavigate();
  const location = useLocation();
  const previewRef = useRef(null);
  const headerDownloadActionRef = useRef(() => {});
  const resumeData = useResumeStore((s) => s.resume);
  const selectedTemplateId = useResumeStore((s) => s.selectedTemplateId);
  const selectedTemplateSource = useResumeStore((s) => s.selectedTemplateSource);
  const setSelectedTemplateId = useResumeStore((s) => s.setSelectedTemplateId);
  const viewportWidth = useViewportWidth();
  const latestResumeRef = useRef(resumeData);
  const templateInitSignatureRef = useRef("");

  const [messageApi, contextHolder] = message.useMessage();
  const isTablet = viewportWidth <= 768;
  const isSmallScreen = viewportWidth <= 1024;
  const deviceType = isTablet ? "mobile" : isSmallScreen ? "tablet" : "desktop";
  const paymentModalShell = "#ffffff";
  const paymentModalBorder = "1px solid #dfe5ec";
  const paymentModalShadow = "0 28px 80px rgba(15, 23, 42, 0.28)";
  const [showTemplates, setShowTemplates] = useState(
    () => new URLSearchParams(location.search).get("templates") === "open",
  );
  const currentUser = sessionStorage.getItem("userName") || "";
  const [isDownloading, setIsDownloading] = useState(false);
  const isEmptyResume = isResumeEmpty(resumeData);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selectedDownloadFormat, setSelectedDownloadFormat] = useState("pdf");
  const { refresh: refreshSubscription } = useSubscriptionStatus();
  const setExportLoading = useResumeStore((state) => state.setExportLoading);
  const selectedExperienceLevel = normalizeExperienceLevel(
    sessionStorage.getItem("experienceLevel"),
  );
  const visibleTemplates = useMemo(
    () =>
      getTemplateOptionsForExperienceLevel(selectedExperienceLevel, {
        smallScreen: isSmallScreen,
      }),
    [isSmallScreen, selectedExperienceLevel],
  );
  const visibleTemplateIds = useMemo(
    () => visibleTemplates.map((templateOption) => templateOption.id),
    [visibleTemplates],
  );
  const visibleTemplateIdsKey = useMemo(
    () => visibleTemplateIds.join("|"),
    [visibleTemplateIds],
  );
  const routeTemplateId = useMemo(
    () => readTemplateIdFromSearch(location.search) || location.state?.templateId || null,
    [location.search, location.state?.templateId],
  );
  const effectiveSelectedTemplateId =
    visibleTemplateIds.includes(selectedTemplateId)
      ? selectedTemplateId
      : visibleTemplateIds[0] || "template1";
  const templateInitOptions = useMemo(
    () => ({
      explicitTemplateId: routeTemplateId,
      fallbackTemplateId: visibleTemplateIds[0] || "template1",
      allowedTemplateIds: visibleTemplateIds,
      scope: "/export",
    }),
    [routeTemplateId, visibleTemplateIds],
  );
  const activeTemplateOption =
    visibleTemplates.find(
      (templateOption) => templateOption.id === effectiveSelectedTemplateId,
    ) ||
    visibleTemplates[0];
  const workspaceBackground =
    "radial-gradient(circle at 18% 14%, rgba(23, 215, 179, 0.16), transparent 26%), radial-gradient(circle at 82% 12%, rgba(183, 134, 71, 0.14), transparent 22%), linear-gradient(180deg, #081320 0%, #0d1830 56%, #09131f 100%)";
  const panelBackground =
    "linear-gradient(180deg, rgba(16, 25, 45, 0.94), rgba(13, 22, 39, 0.9))";
  const panelBorder = "1px solid rgba(95, 126, 162, 0.26)";
  const panelShadow = "0 28px 80px rgba(2, 6, 23, 0.46)";
  const previewShellBackground =
    "linear-gradient(180deg, rgba(15, 23, 42, 0.94), rgba(18, 24, 38, 0.92))";

  useEffect(() => {
    latestResumeRef.current = resumeData;
  }, [resumeData]);

  useEffect(() => {
    if (!resumeData) return;
    if (!isEmptyResume) return;

    messageApi.warning("Please fill resume details before Preview & Download.");
    navigate("/resume/new", { replace: true });
  }, [isEmptyResume, messageApi, navigate, resumeData]);

  useEffect(() => {
    const initSignature = [
      routeTemplateId || "",
      visibleTemplateIdsKey,
      templateInitOptions.fallbackTemplateId || "",
    ].join("::");

    if (templateInitSignatureRef.current === initSignature) {
      return;
    }

    templateInitSignatureRef.current = initSignature;
    useResumeStore.getState().initializeSelectedTemplate(templateInitOptions);
  }, [
    templateInitOptions,
    routeTemplateId,
    visibleTemplateIdsKey,
  ]);

  useEffect(() => {
    if (!effectiveSelectedTemplateId) return;

    const currentSearchTemplateId = readTemplateIdFromSearch(location.search);
    if (currentSearchTemplateId === effectiveSelectedTemplateId) return;

    navigate(
      {
        pathname: location.pathname,
        search: withTemplateIdInSearch(location.search, effectiveSelectedTemplateId),
      },
      {
        replace: true,
        state: {
          ...(location.state || {}),
          templateId: effectiveSelectedTemplateId,
        },
      },
    );
  }, [
    effectiveSelectedTemplateId,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    logTemplateSelection("/export:route", {
      selectedTemplateId: effectiveSelectedTemplateId,
      source: selectedTemplateSource,
      routeTemplateId,
    });
  }, [effectiveSelectedTemplateId, routeTemplateId, selectedTemplateSource]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const templatesOpen = params.get("templates") === "open";

    if (isSmallScreen && templatesOpen) {
      params.delete("templates");
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : "",
        },
        {
          replace: true,
          state: location.state,
        },
      );
      setShowTemplates(false);
      return;
    }

    setShowTemplates(!isSmallScreen && templatesOpen);
  }, [isSmallScreen, location.pathname, location.search, location.state, navigate]);

  const getMissingImportantFields = (data) => {
    if (!data || typeof data !== "object") return ["Resume Data"];

    return getMissingRequiredFields(data);
  };

  const downloadFileName =
    resumeData?.name?.trim().replace(/[\\/:*?"<>|]+/g, " ") || "Resume";

  const getDownloadFileName = () => downloadFileName;
  const paymentPurchaseContext = useMemo(
    () =>
      buildDownloadPurchaseContext({
        userName: currentUser,
        fileName: downloadFileName,
        format: selectedDownloadFormat,
        downloadLabel: selectedDownloadFormat.toUpperCase(),
        source: "/export",
      }),
    [currentUser, downloadFileName, selectedDownloadFormat],
  );

  const triggerDownload = (blob, fileName, extension) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${fileName}.${extension}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const buildDocumentHtml = (previewNode) => {
    const exportNode = buildExportNode(previewNode);
    const pdfPrintCss = buildPdfPrintCss(previewNode, effectiveSelectedTemplateId);

    return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8" />
  <title>${getDownloadFileName()}</title>
  ${pdfPrintCss}
  <style>
    body {
      margin: 0;
      background: #ffffff;
    }

    .resume-print {
      box-shadow: none !important;
    }
  </style>
</head>
<body>
  ${exportNode?.outerHTML || previewNode.outerHTML}
</body>
</html>`;
  };

  const buildMeasuredPdfHtml = async (previewNode) => {
    const pdfPrintCss = buildPdfPrintCss(
      previewNode,
      effectiveSelectedTemplateId,
    );
    const exportNode = await buildMeasuredExportNode(previewNode);

    if (exportNode) {
      exportNode.style.width = `${FIXED_A4_WIDTH_MM}mm`;
      exportNode.style.maxWidth = `${FIXED_A4_WIDTH_MM}mm`;
      exportNode.style.minHeight = "auto";
      exportNode.style.height = "auto";
      exportNode.style.margin = "0";
      exportNode.style.padding = "0";
      exportNode.style.border = "0";
      exportNode.style.boxShadow = "none";
      exportNode.style.background = "#ffffff";
    }

    return `${pdfPrintCss}${exportNode?.outerHTML || previewNode.outerHTML}`;
  };

  const downloadPDF = async () => {
    if (isEmptyResume) {
      messageApi.warning("Please fill resume details before downloading.");
      setExportLoading(false);
      return;
    }
    if (isDownloading) {
      setExportLoading(false);
      return;
    }
    const previewNode = previewRef.current;
    if (!previewNode) {
      messageApi.error("Preview is not ready. Please try again.");
      setExportLoading(false);
      return;
    }
    const fileName = getDownloadFileName();
    setIsDownloading(true);
    try {
      const htmlContent = await buildMeasuredPdfHtml(previewNode);
      const response = await authenticatedFetch(API.GENERATE_PDF, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          html: htmlContent,
          resumeData,
          templateId: effectiveSelectedTemplateId,
          fileName,
          scale: 1,
          userName: currentUser,
        }),
      });
      if (!response.ok) {
        const text = await response.text();
        console.error("PDF generation failed:", text);
        messageApi.error("PDF generation failed. Please try again.");
        return;
      }
      const blob = await response.blob();
      if (blob.size === 0) {
        messageApi.error("Generated PDF is empty.");
        return;
      }
      triggerDownload(blob, fileName, "pdf");
      messageApi.success("PDF downloaded successfully.");
    } catch (err) {
      console.error("Download error:", err);
      messageApi.error("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
      setExportLoading(false);
    }
  };

  const downloadDoc = async () => {
    if (isEmptyResume) {
      messageApi.warning("Please fill resume details before downloading.");
      setExportLoading(false);
      return {
        ok: false,
        reason: "validation",
        message: "Please fill resume details before downloading.",
      };
    }
    if (isDownloading) {
      setExportLoading(false);
      return { ok: false, reason: "busy", message: "Download already in progress." };
    }

    const previewNode = previewRef.current;
    if (!previewNode) {
      messageApi.error("Preview is not ready. Please try again.");
      setExportLoading(false);
      return {
        ok: false,
        reason: "preview",
        message: "Preview is not ready. Please try again.",
      };
    }

    const fileName = getDownloadFileName();
    setIsDownloading(true);

    try {
      const htmlContent = buildDocumentHtml(previewNode);
      const blob = new Blob([htmlContent], {
        type: "application/msword;charset=utf-8",
      });

      triggerDownload(blob, fileName, "doc");
      messageApi.success("DOC downloaded successfully.");
      return { ok: true };
    } catch (err) {
      console.error("DOC download error:", err);
      return {
        ok: false,
        reason: "client",
        message: "DOC download failed. Please try again.",
      };
    } finally {
      setIsDownloading(false);
      setExportLoading(false);
    }
  };

  const downloadDocx = async () => {
    if (isEmptyResume) {
      messageApi.warning("Please fill resume details before downloading.");
      setExportLoading(false);
      return {
        ok: false,
        reason: "validation",
        message: "Please fill resume details before downloading.",
      };
    }
    if (isDownloading) {
      setExportLoading(false);
      return { ok: false, reason: "busy", message: "Download already in progress." };
    }

    const fileName = getDownloadFileName();
    setIsDownloading(true);

    try {
      const response = await authenticatedFetch(API.GENERATE_DOCX, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileName,
          resumeData,
          templateId: effectiveSelectedTemplateId,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        console.error("DOCX generation failed:", text);
        return {
          ok: false,
          reason: "server",
          message: "Download failed due to a server issue. Please try again.",
          statusCode: response.status,
          details: text,
        };
      }

      const blob = await response.blob();
      triggerDownload(blob, fileName, "docx");
      messageApi.success("DOCX downloaded successfully.");
      return { ok: true };
    } catch (err) {
      console.error("DOCX download error:", err);
      return {
        ok: false,
        reason: "server",
        message: "Download failed due to a server issue. Please try again.",
      };
    } finally {
      setIsDownloading(false);
      setExportLoading(false);
    }
  };

  const downloadSelectedFormat = async (format = "pdf") => {
    if (format === "doc") {
      return downloadDoc();
    }

    if (format === "docx") {
      return downloadDocx();
    }

    return downloadPDF();
  };

  const handleDownloadClick = async (format = "pdf") => {
    if (isEmptyResume) {
      messageApi.warning("Please fill resume details before downloading.");
      return;
    }

    const missingFields = getMissingImportantFields(resumeData);

    if (missingFields.length > 0) {
      messageApi.error(
        `Please fix these fields before downloading: ${missingFields.join(", ")}`,
      );
      setExportLoading(false);
      return;
    }
    setSelectedDownloadFormat(format);
    try {
      const subscription = await refreshSubscription({ silent: true });
      if (subscription?.active) {
        await downloadSelectedFormat(format);
        return;
      }

      if (subscription?.status === "expired") {
        messageApi.warning(
          "Your 7-day subscription has expired. Renew for ₹100 to continue using premium features.",
        );
      }
      setExportLoading(false);
      setPaymentOpen(true);
    } catch (error) {
      messageApi.error(error.message || "Unable to verify subscription status.");
      setExportLoading(false);
    }
  };

  useEffect(() => {
    headerDownloadActionRef.current = handleDownloadClick;
  });

  useEffect(() => {
    const handleHeaderDownload = (event) => {
      headerDownloadActionRef.current(event?.detail?.format || "pdf");
    };
    window.addEventListener("cvcanvas:export-download", handleHeaderDownload);
    return () => {
      window.removeEventListener(
        "cvcanvas:export-download",
        handleHeaderDownload,
      );
    };
  }, []);

  useEffect(() => {
    const pendingAction = location.state?.resumePremiumAction;
    if (!pendingAction) return;

    const actionFormats = {
      download: "pdf",
      "download-pdf": "pdf",
      "download-docx": "docx",
      export: "pdf",
    };
    const pendingFormat = actionFormats[pendingAction];

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: {
        ...location.state,
        resumePremiumAction: null,
      },
    });

    if (pendingFormat) {
      headerDownloadActionRef.current(pendingFormat);
    }
  }, [location.pathname, location.search, location.state, navigate]);

  if (!resumeData) {
    return <div style={{ padding: 40 }}>No resume data found.</div>;
  }

  const handlePaymenrtModel = () => {
    setPaymentOpen((prev) => !prev);
    setExportLoading(false);
  };

  return (
    <Layout
      style={{
        minHeight: "100vh",
        background: workspaceBackground,
        overflow: "visible",
      }}
    >
      {contextHolder}
      <Modal
        open={paymentOpen}
        footer={null}
        onCancel={handlePaymenrtModel}
        centered
        width={isTablet ? "calc(100vw - 24px)" : 620}
        className="payment-modal-panel"
        wrapClassName={isTablet ? "payment-modal-wrap payment-modal-wrap--mobile" : "payment-modal-wrap"}
        closeIcon={
          <span style={{ color: "#64748b", fontSize: 22 }}>
            x
          </span>
        }
        styles={{
          mask: {
            backdropFilter: "blur(8px)",
          },
          content: {
            background: paymentModalShell,
            border: paymentModalBorder,
            borderRadius: 12,
            boxShadow: paymentModalShadow,
            padding: isTablet ? "26px 20px 22px" : "32px",
          },
          header: {
            background: "transparent",
          },
          body: {
            background: "transparent",
          },
        }}
      >
        <PaymentStatus
          setPaymentOpen={setPaymentOpen}
          downloadLabel={selectedDownloadFormat.toUpperCase()}
          onPaymentSuccess={() => downloadSelectedFormat(selectedDownloadFormat)}
          purchaseContext={paymentPurchaseContext}
        />
      </Modal>
      {/* MAIN CONTENT */}
      <Content
        style={{
          flex: 1,
          padding: isTablet ? 12 : 24,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          background: "transparent",
          overflow: "visible",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isTablet ? "column" : "row",
            gap: 12,
            width: "100%",
            maxWidth: 1520,
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          {/* RESUME PREVIEW */}
          <div
            style={{
              flex: isTablet
                ? "1 1 auto"
                : showTemplates
                  ? "0 1 60%"
                  : "1 1 auto",
              width: isTablet ? "100%" : undefined,
              minWidth: 0,
              minHeight: "auto",
              height: "auto",
              overflow: "visible",
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                width: isTablet ? "100%" : "fit-content",
                maxWidth: "100%",
                padding: isTablet ? 12 : 16,
                borderRadius: 30,
                background: previewShellBackground,
                border: panelBorder,
                boxShadow: panelShadow,
                boxSizing: "border-box",
                backdropFilter: "blur(18px)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle at 16% 12%, rgba(23, 215, 179, 0.14), transparent 24%), radial-gradient(circle at 86% 10%, rgba(183, 134, 71, 0.12), transparent 20%)",
                  pointerEvents: "none",
                }}
              />
              <div
                className="no-print"
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: isTablet ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isTablet ? "flex-start" : "center",
                  gap: 14,
                  marginBottom: 14,
                  padding: isTablet ? "2px 2px 0" : "2px 4px 0",
                }}
              >
                <div>
                  <div
                    style={{
                      color: "#f8fafc",
                      fontSize: 21,
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Live Preview
                  </div>
                  {/* <div
                    style={{
                      color: "rgba(191, 204, 223, 0.88)",
                      fontSize: 13,
                      marginTop: 4,
                      lineHeight: 1.5,
                    }}
                  >
                    Hybrid preview styling with premium tone, cleaner structure, and stronger gallery energy.
                  </div> */}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      padding: "7px 12px",
                      borderRadius: 999,
                      background: "rgba(23, 215, 179, 0.12)",
                      border: "1px solid rgba(126, 247, 228, 0.3)",
                      color: "#dffef8",
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                    }}
                  >
                    ATS SAFE
                  </span>
                  <span
                    style={{
                      padding: "7px 12px",
                      borderRadius: 999,
                      background: "rgba(183, 134, 71, 0.14)",
                      border: "1px solid rgba(224, 195, 146, 0.28)",
                      color: "#f2dfc4",
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                    }}
                  >
                    PREMIUM
                  </span>
                  {activeTemplateOption?.name ? (
                    <span
                      style={{
                        padding: "7px 12px",
                        borderRadius: 999,
                        background: "rgba(37, 99, 235, 0.12)",
                        border: "1px solid rgba(125, 166, 255, 0.24)",
                        color: "#dbeafe",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {activeTemplateOption.name}
                    </span>
                  ) : null}
                </div>
              </div>
              <div
                ref={previewRef}
                className="resume-print"
                data-template-id={effectiveSelectedTemplateId}
                style={{
                  width: isTablet
                    ? "100%"
                    : `min(100%, ${FIXED_A4_WIDTH_MM}mm)`,
                  maxWidth: `${FIXED_A4_WIDTH_MM}mm`,
                  minHeight: isTablet ? "auto" : `${BASE_A4_HEIGHT_MM}mm`,
                  height: "fit-content",
                  padding: isTablet ? "4mm 1mm" : "0",
                  background: "#ffffff",
                  boxSizing: "border-box",
                  borderRadius: 0,
                  border: "1px solid rgba(212, 222, 237, 0.92)",
                  boxShadow: "0 18px 44px rgba(15, 23, 42, 0.22)",
                  transformOrigin: "top center",
                  margin: "0 auto",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <TemplateRenderer templateId={effectiveSelectedTemplateId} />
              </div>
            </div>
          </div>

          {/* TEMPLATE SIDEBAR */}
          {showTemplates && !isSmallScreen && (
            <div
              className="no-print"
              style={{
                width: isTablet ? "100%" : "40%",
                height: "auto",
                maxHeight: isTablet ? "none" : "calc(100vh - 96px)",
                overflowY: isTablet ? "visible" : "auto",
                overflowX: "hidden",
                flexShrink: 0,
                minWidth: isTablet ? "auto" : 420,
                background: panelBackground,
                border: panelBorder,
                padding: 10,
                borderRadius: 28,
                boxShadow: panelShadow,
                backdropFilter: "blur(18px)",
                position: isTablet ? "static" : "sticky",
                top: isTablet ? "auto" : 24,
              }}
            >
              <div style={{ marginBottom: 18 }}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 12px",
                    marginBottom: 12,
                    borderRadius: 999,
                    background: "rgba(23, 215, 179, 0.1)",
                    border: "1px solid rgba(113, 232, 212, 0.18)",
                    color: "#dffef8",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                  }}
                >
                  CURATED GALLERY
                </div>
                <div
                  style={{
                    color: "#f8fafc",
                    fontSize: 28,
                    fontWeight: 700,
                    marginBottom: 6,
                    letterSpacing: "-0.03em",
                  }}
                >
                  Template Gallery
                </div>
                </div>
                <div
                  style={{
                    color: "#a7b7ca",
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  Compare the live preview before downloading. The page styling is polished, but the template content stays true to the selected design.
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isTablet
                    ? "1fr"
                    : "repeat(3, minmax(0, 1fr))",
                  gap: 16,
                  padding: isTablet ? "0" : "0px 1em 0px 0px",
                  boxSizing: "border-box",
                }}
              >
                {visibleTemplates.map((templateOption) => (
                  <button
                    key={templateOption.id}
                    type="button"
                    onClick={() =>
                      setSelectedTemplateId(templateOption.id, "/export:gallery")
                    }
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                      width: "100%",
                      display: "grid",
                      gap: 6,
                      padding: 6,
                      boxSizing: "border-box",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 6,
                        background:
                          effectiveSelectedTemplateId === templateOption.id
                            ? "linear-gradient(180deg, rgba(14,22,38,0.98) 0%, rgba(21,34,57,0.95) 100%)"
                            : "linear-gradient(180deg, rgba(15,23,42,0.92) 0%, rgba(17,27,46,0.86) 100%)",
                        border:
                          effectiveSelectedTemplateId === templateOption.id
                            ? "2px solid rgba(110, 231, 212, 0.9)"
                            : "1px solid rgba(63, 91, 125, 0.72)",
                        borderRadius: 0,
                        boxShadow:
                          effectiveSelectedTemplateId === templateOption.id
                            ? "0 14px 26px rgba(6, 12, 24, 0.3)"
                            : "0 8px 18px rgba(6, 12, 24, 0.16)",
                        contentVisibility: "auto",
                        containIntrinsicSize: "120px 180px",
                      }}
                    >
                      <TemplateLivePreview
                        templateId={templateOption.id}
                        variant="sidebar"
                        borderRadius={0}
                      />
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 13,
                        fontWeight: 700,
                        color:
                          effectiveSelectedTemplateId === templateOption.id
                            ? "#f8fafc"
                            : "#d4dce8",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {templateOption.name}
                    </div>

                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </Content>

      {/* PRINT STYLES */}
      <style>
        {`
@page {
  size: A4;
  margin: 8mm;
}

.resume-print {
  width: 210mm;
}

.companyBlock,
.sectionBlock,
.timelineItem,
.exp-block,
.cv-group,
[data-cv-group="true"] {
  break-inside: auto;
  page-break-inside: auto;
  max-height: none !important;
  overflow: visible !important;
}

[data-pdf-export="true"] [data-section="experienceItem"]:not([data-cv-split-allowed="true"]),
[data-pdf-export="true"] [data-section="projectItem"]:not([data-cv-split-allowed="true"]),
[data-pdf-export="true"] .exp-block:not([data-cv-split-allowed="true"]),
[data-pdf-export="true"] .companyBlock:not([data-cv-split-allowed="true"]),
[data-pdf-export="true"] .timelineItem:not([data-cv-split-allowed="true"]) {
  break-inside: avoid;
  page-break-inside: avoid;
}

[data-pdf-export="true"] [data-cv-split-allowed="true"] {
  break-inside: auto;
  page-break-inside: auto;
}

.eduRowBlock,
.headerBox,
.cv-section[data-cv-keep="whole"],
.cv-heading,
[data-section="educationItem"],
[data-section="awardItem"],
[data-section="recognitionItem"] {
  break-inside: avoid;
  page-break-inside: avoid;
  max-height: none !important;
  overflow: visible !important;
}

[data-resume-root="true"]:not([data-template-family="new-prototype"]) section,
[data-resume-root="true"]:not([data-template-family="new-prototype"]) .cv-section,
[data-high-style-section="true"],
[data-resume-root="true"]:not([data-template-family="new-prototype"]) .section {
  border-top: 1px solid transparent;
  padding-top: 6px;
  margin-bottom: 12px;
  page-break-inside: auto;
}

[data-resume-root="true"]:not([data-template-family="new-prototype"]) .cv-section + .cv-section,
[data-high-style-section="true"] + [data-high-style-section="true"],
[data-resume-root="true"]:not([data-template-family="new-prototype"]) .section + .section,
[data-resume-root="true"]:not([data-template-family="new-prototype"]) [data-section] + [data-section] {
  margin-top: 14px;
  border-top-color: rgba(148, 163, 184, 0.14);
}

[data-template-family="high-style"] [data-high-style-body] {
  display: flex !important;
  flex-direction: row !important;
  gap: 20px !important;
  align-items: flex-start !important;
  page-break-inside: auto !important;
  break-inside: auto !important;
}

[data-template-family="high-style"] [data-high-style-main],
.cv-main {
  display: block !important;
  flex: 0 0 65% !important;
  width: 65% !important;
}

[data-template-family="high-style"] [data-high-style-sidebar],
.cv-sidebar {
  display: block !important;
  flex: 0 0 35% !important;
  width: 35% !important;
}

@media print {

  .no-print {
    display: none !important;
  }

  [data-resume-root="true"]:not([data-template-family="new-prototype"]) section,
  [data-resume-root="true"]:not([data-template-family="new-prototype"]) .cv-section,
  [data-high-style-section="true"],
  [data-resume-root="true"]:not([data-template-family="new-prototype"]) .section {
    padding-top: 8px;
  }

  [data-resume-root="true"]:not([data-template-family="new-prototype"]) section:first-child,
  [data-resume-root="true"]:not([data-template-family="new-prototype"]) .cv-section:first-child,
  [data-high-style-section="true"]:first-child,
  [data-resume-root="true"]:not([data-template-family="new-prototype"]) .section:first-child {
    padding-top: 0;
  }
}
.ant-modal .ant-modal-close-x,
.ant-modal .ant-modal-close {
  color: #e2e8f0;
}
  .ant-modal-header{
  border-bottom:0px
  }
`}
      </style>
    </Layout>
  );
}
